// DonationsContext.tsx
import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useCallback,
} from 'react';
import {
  initialTopDonations,
  initialRecentDonations,
  INITIAL_DATA_VERSION,
} from '../data/initialDonations';

interface Donation {
  name: string;
  amount: number;
  date?: string;
}

interface DonationsContextType {
  topDonations: Donation[];
  recentDonations: Donation[];
  totalDonated: number | null;
  isLoading: boolean;
  isRefreshing: boolean;
  error: string | null;
  hasFullData: boolean;
  refreshDonations: () => Promise<void>;
  loadFullData: () => Promise<void>;
}

const DonationsContext = createContext<DonationsContextType | undefined>(
  undefined
);

export const DonationsProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [topDonations, setTopDonations] = useState<Donation[]>([]);
  const [recentDonations, setRecentDonations] = useState<Donation[]>([]);
  const [totalDonated] = useState<number | null>(null);

  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasFullData, setHasFullData] = useState(false);

  // Al montar el provider, inicializamos state con datos iniciales locales
  useEffect(() => {
    // Siempre usar datos iniciales locales sin intentar cargar del servidor
    setTopDonations(initialTopDonations);
    setRecentDonations(initialRecentDonations);
    setHasFullData(true);
    setIsLoading(false);
    
    // Guardar en localStorage para persistencia
    localStorage.setItem('donationsVersion', INITIAL_DATA_VERSION.toString());
    localStorage.setItem('topDonations', JSON.stringify(initialTopDonations));
    localStorage.setItem('recentDonations', JSON.stringify(initialRecentDonations));
    localStorage.setItem('donationsHasFullData', 'true');
  }, []);

  // No intentamos cargar datos del servidor - siempre usamos datos locales
  // useEffect(() => {
  //   if (!configIsLoaded || !version) return;
  //   // ... código eliminado
  // }, [configIsLoaded, version, fetchDonationsFromServer]);

  // Refresco manual - solo usa datos locales
  const refreshDonations = useCallback(async () => {
    setIsRefreshing(true);
    // Simplemente usamos los datos locales ya que no dependemos del servidor
    setTimeout(() => {
      setIsRefreshing(false);
    }, 500);
  }, []);

  // Carga completa - solo usa datos locales
  const loadFullData = useCallback(async () => {
    setIsRefreshing(true);
    // Simplemente usamos los datos locales ya que no dependemos del servidor
    setTimeout(() => {
      setIsRefreshing(false);
    }, 500);
  }, []);

  return (
    <DonationsContext.Provider
      value={{
        topDonations,
        recentDonations,
        totalDonated,
        isLoading,
        isRefreshing,
        error,
        hasFullData,
        refreshDonations,
        loadFullData,
      }}
    >
      {children}
    </DonationsContext.Provider>
  );
};

export const useDonations = () => {
  const ctx = useContext(DonationsContext);
  if (!ctx) {
    throw new Error('useDonations must be used within a DonationsProvider');
  }
  return ctx;
};
